/**
 * IptcConfigStep - Step 3 of match creation wizard
 *
 * Configures IPTC metadata templates for the match:
 * - Template fields with variable insertion
 * - Live preview of rendered values (using real match data from Step 1)
 * - Location fields (pre-filled from Step 1)
 * - Keywords input
 * - Category and Supplemental Categories
 * - Load from company defaults button
 * - Auto-prefill from match data (step 1)
 * - Auto-generated title in HOME_AWAY_CODE_DDMMYY format
 *
 * SCOPE. The form edits one of two things depending on the destination
 * switcher at the top:
 *
 *   All destinations  ->  the match config: the shared base, applied everywhere
 *   a single destination -> that destination's overrides: changed keys only
 *
 * There are not three modes here despite there being three ways to use it.
 * Edit the base and every destination follows; edit the base and then override
 * one; override every one. That is one mechanism used three ways, and building
 * it as three would have been the mistake.
 *
 * RESOLUTION is override > this destination's template > the shared base.
 * A destination that has been given a template keeps that template's rendering
 * when the base is edited; "every destination follows the base" holds for the
 * destinations naming no template, and for every field a template leaves blank.
 *
 * Only the RENDERINGS are per destination. The facts about the match -- where
 * it was, what it was called, who holds copyright -- are the same wherever the
 * frame goes, so those sections are only shown on the base.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import TagInput from '../../../../shared/components/ui/TagInput/TagInput';
import Button from '../../../../shared/components/ui/Button';
import { useAuth } from '../../../../auth.jsx';
import {
  MONTHS,
  computeSeason,
  getCompetitionLabel,
  getCompetitionShortCode,
} from '../../utils/constants';
import useCompetitions from '../../hooks/useCompetitions';
import styles from './MatchModal.module.css';

// The fields a single destination may disagree with the rest on. Mirrors
// OVERRIDABLE_IPTC_FIELDS server-side, which REJECTS anything outside it --
// keep the two in step or the form will offer edits the API refuses.
//
// `copyright_notice` was absent until 09-08-2026 on the reasoning that
// "copyright stays with the photographer". Overruled: this is the IPTC
// CopyrightNotice STRING, not an assignment of copyright, and a wire may
// legitimately need its own notice -- Getty wants
// `{photog}/MB Media/Getty Images` where the house line says Box to Box.
// The location and event facts stay out, and that boundary still holds: they
// are facts about the match, and a destination inventing its own venue is
// nonsense.
const OVERRIDABLE_FIELDS = new Set([
  'title_template',
  'headline_template',
  'caption_template',
  'keywords',
  'credit',
  'source',
  'copyright_notice',
  // dc:Creator / By-line. Getty reads By-line as the credited party and wants
  // the AGENCY there while the caption still names the person. Mirrors
  // OVERRIDABLE_IPTC_FIELDS server-side. Added 2026-08-19.
  'creator',
  'category',
  'supplemental_categories',
  // How each tagged person is phrased. Rendered once per ENTRY, so three
  // people in a frame is three entries in one file -- never three deliveries.
  'persons_template',
  'organisation_template',
  // Wire references. A destination names ONE of these as the field it wants a
  // per-match code in; they were misfiled as match facts at first, but a
  // per-event code is meaningful to one wire and nobody else. Mirrors
  // REFERENCE_FIELDS server-side.
  'transmission_reference',
  'instructions',
  'urgency',
  'authors_position',
  'caption_writer',
]);

// The overridable fields that hold a LIST rather than a string. An empty
// destination-scope input has to be `[]` for these and `''` for the rest, or
// the chip editors receive a string and render one chip per character.
const ARRAY_FIELDS = new Set(['keywords', 'supplemental_categories']);

// Step-3 field -> the OUTPUT metadata key a template suppresses. They are not
// the same names: the form edits `caption_template`, the exporter pops
// `description`. Without this map a suppressed caption would look editable.
const METADATA_KEY_FOR_FIELD = {
  title_template: 'title',
  headline_template: 'headline',
  caption_template: 'description',
  keywords: 'subject',
  credit: 'credit',
  source: 'source',
  copyright_notice: 'rights',
  category: 'category',
  supplemental_categories: 'supplemental_categories',
  persons_template: 'persons_in_image',
  organisation_template: 'organisation_in_image',
};

// Available template variables with descriptions
const TEMPLATE_VARIABLES = [
  { name: 'home_team', label: 'Home Team', example: 'Feyenoord Rotterdam' },
  { name: 'away_team', label: 'Away Team', example: 'AFC Ajax' },
  { name: 'event', label: 'Event Name', example: 'Feyenoord vs Ajax' },
  { name: 'competition', label: 'Competition', example: 'Eredivisie' },
  { name: 'season', label: 'Season', example: '2025/2026' },
  { name: 'round', label: 'Round', example: '12th' },
  { name: 'venue', label: 'Venue', example: 'Stadion Feijenoord' },
  { name: 'location', label: 'Location / Sublocation', example: 'De Kuip' },
  { name: 'city', label: 'City', example: 'Rotterdam' },
  { name: 'state', label: 'State / Province', example: 'Zuid-Holland' },
  { name: 'country', label: 'Country', example: 'Netherlands' },
  { name: 'team', label: 'Team (non-match events)', example: 'Feyenoord Rotterdam' },
  { name: 'day', label: 'Day', example: '26' },
  { name: 'day0', label: 'Day (zero-padded)', example: '03' },
  { name: 'month', label: 'Month (numeric)', example: '01' },
  { name: 'monthname', label: 'Month Name', example: 'January' },
  { name: 'year4', label: 'Year', example: '2026' },
  { name: 'photog', label: 'Photographer', example: 'John Doe' },
  { name: 'persons', label: 'Tagged Persons', example: 'Player 1, Player 2' },
  { name: 'match_phase', label: 'Match Phase', example: 'during' },
  { name: 'copyright', label: 'Copyright', example: 'VK Sportphoto' },
  { name: 'copyrighturl', label: 'Copyright URL', example: 'https://heroimages.nl' },
  { name: 'company', label: 'Company', example: 'VK Sportphoto' },
  // The channel identity trio. These resolve in a fixed order server-side --
  // copyright, then credit, then source -- so a caption may reference an
  // earlier one but not a later one. Substitution is a single pass.
  { name: 'credit', label: 'Credit', example: 'Getty Images' },
  { name: 'source', label: 'Source', example: 'Getty Images Europe' },
];

// Preview field display labels
const PREVIEW_LABELS = {
  title_template: 'Title',
  headline_template: 'Headline',
  caption_template: 'Caption',
  event_name: 'Event Name',
  keywords: 'Keywords',
  credit: 'Credit',
  copyright_notice: 'Copyright',
  creator: 'By-line',
  category: 'Category',
  supplemental_categories: 'Suppl. Categories',
  country_code: 'Country Code',
};

/**
 * Variable dropdown for inserting template variables
 */
function VariableDropdown({ onInsert }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={styles.variableDropdown}>
      <button
        type="button"
        className={styles.variableButton}
        onClick={() => setIsOpen(!isOpen)}
      >
        + Variable
      </button>
      {isOpen && (
        <div className={styles.variableMenu}>
          {TEMPLATE_VARIABLES.map((v) => (
            <button
              key={v.name}
              type="button"
              className={styles.variableItem}
              onClick={() => {
                onInsert(`{${v.name}}`);
                setIsOpen(false);
              }}
            >
              <span className={styles.variableName}>{`{${v.name}}`}</span>
              <span className={styles.variableLabel}>{v.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * SEED-ON-FOCUS for an inherited field.
 *
 * On a destination an un-overridden field is EMPTY and shows what it inherits as
 * the input's `placeholder`. A placeholder disappears on the first keystroke, so
 * changing one word of an inherited caption meant retyping it or copy-pasting the
 * whole string out of the base form first.
 *
 * Focusing such a field now fills the box with the inherited text so it can be
 * EDITED. This is the same bargain the chip fields already make ("adding or
 * removing one chip adopts the inherited set as this destination's own"); the
 * text fields never got it.
 *
 * `draft` is deliberately LOCAL and is never written to the override map.
 * Tabbing through a form must not mint overrides, and the
 * "FOLLOWS BASE - NO OVERRIDES" badge must not flip on focus and then flip back.
 * Nothing is stored until the operator actually types, at which point the normal
 * onChange path runs and the field becomes an override like any other. Blur
 * without an edit throws the seed away, leaving the field genuinely empty and
 * still inheriting.
 *
 * Shared by both input shapes on this screen so the rule cannot drift between
 * the template boxes and the plain ones.
 */
function useInheritedDraft({ value, inherited, overridden, onChange }) {
  const [draft, setDraft] = useState(null);
  const shown = draft !== null ? draft : value;

  const commit = (next) => {
    setDraft(null);
    onChange(next);
  };

  return {
    shown,
    commit,
    fieldProps: {
      value: shown,
      onChange: (e) => commit(e.target.value),
      onFocus: () => {
        if (!overridden && !value && inherited) setDraft(inherited);
      },
      onBlur: () => setDraft(null),
    },
  };
}

/**
 * A plain override text box that seeds from what it inherits. Same rule as
 * TemplateInput, minus the variable dropdown.
 */
function OverrideInput({
  className,
  value,
  inherited = '',
  overridden = false,
  onChange,
  placeholder,
  ...rest
}) {
  const { fieldProps } = useInheritedDraft({ value, inherited, overridden, onChange });
  return (
    <input
      type="text"
      className={className}
      placeholder={placeholder}
      {...rest}
      {...fieldProps}
    />
  );
}

/**
 * Template input with variable insertion dropdown
 */
function TemplateInput({
  label,
  value,
  onChange,
  placeholder,
  inherited = '',
  multiline = false,
  overridden = false,
  onRevert,
}) {
  const { shown, fieldProps, commit } = useInheritedDraft({
    value,
    inherited,
    overridden,
    onChange,
  });

  // Insert against what is on SCREEN, not the stored value: appending a variable
  // to a seeded field should extend the inherited text, not replace it with a
  // lone token.
  const handleInsert = (variable) => {
    commit(shown + variable);
  };

  const InputComponent = multiline ? 'textarea' : 'input';
  const base = multiline ? styles.textarea : styles.input;
  // The tint IS the override marking -- no label dot, no badge. One signal,
  // on the thing that actually differs.
  const className = overridden ? `${base} ${styles.inputOverridden}` : base;

  return (
    <div className={styles.formGroup}>
      <div className={styles.templateHeader}>
        {onRevert ? (
          <OverrideLabel label={label} overridden={overridden} onRevert={onRevert} />
        ) : (
          <label className={styles.label}>{label}</label>
        )}
        <VariableDropdown onInsert={handleInsert} />
      </div>
      <InputComponent
        type={multiline ? undefined : 'text'}
        className={className}
        placeholder={placeholder}
        rows={multiline ? 3 : undefined}
        {...fieldProps}
      />
    </div>
  );
}
/**
 * Destination rail. Chosen over pills and tabs because a vertical list of
 * destinations reads as a set of things you move BETWEEN, where a pill row read
 * as a filter applied to one thing.
 *
 * The base is deliberately a different KIND of item, not the first entry in the
 * same list: it is the shared value everything else falls back to. It never uses
 * phosphor -- phosphor marks override scope, and using it in both places would
 * make the one signal that matters ambiguous.
 */
function ScopeNavRail({ destinations, activeTarget, onSelect, overridesByTarget, iptcByTarget }) {
  const followers = destinations.filter(
    (d) => Object.keys(overridesByTarget[String(d.id)] || {}).length === 0
  ).length;

  return (
    <nav className={styles.scopeRail} aria-label="IPTC scope">
      <button
        type="button"
        className={`${styles.scopeBaseItem} ${
          activeTarget === null ? styles.scopeBaseItemActive : ''
        }`}
        onClick={() => onSelect(null)}
        aria-pressed={activeTarget === null}
      >
        <span className={styles.scopeBaseIcon} aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
          </svg>
        </span>
        <span className={styles.scopeBaseText}>
          <span className={styles.scopeBaseName}>All destinations</span>
          <span className={styles.scopeBaseMeta}>
            {followers} of {destinations.length} follow the base
          </span>
        </span>
      </button>

      <div className={styles.scopeRailDivider}>
        <span className={styles.scopeRailDividerLabel}>Overrides</span>
      </div>

      {destinations.map((d) => {
        const id = String(d.id);
        const count = Object.keys(overridesByTarget[id] || {}).length;
        const hasTemplate = !!iptcByTarget[id];
        const active = String(activeTarget) === id;
        // A destination that declares it needs a per-match code, without one.
        // Shown here too because placement alone does not stop it being
        // forgotten -- it has to be visible on the step you are actually on.
        const spec = d.reference_spec;
        const needsReference = !!spec && !(overridesByTarget[id] || {})[spec.field];
        return (
          <button
            key={id}
            type="button"
            className={`${styles.scopeRailItem} ${active ? styles.scopeRailItemActive : ''}`}
            onClick={() => onSelect(id)}
            aria-pressed={active}
          >
            <span className={styles.scopeRailItemText}>
              <span className={styles.scopeRailItemName}>
                {d.name}
                {d.selects_only && <span className={styles.selectsBadge}>Selects only</span>}
              </span>
              <span className={styles.scopeRailItemKind}>
                {hasTemplate ? 'Template' : 'House default'}
              </span>
            </span>
            {needsReference && (
              <span
                className={styles.scopeRailWarn}
                title={`${spec.label} is missing for this destination`}
              >
                !
              </span>
            )}
            {count > 0 && <span className={styles.scopeRailBadge}>{count}</span>}
          </button>
        );
      })}
    </nav>
  );
}

/**
 * Adds the shared base's entries to a destination's list field.
 *
 * A list has no conflict to resolve the way a credit or a caption does, so the
 * question "template OR base" has a third answer here: both. That answer is a
 * BUTTON rather than the default, because resolution stays winner-takes-all --
 * a template naming keywords means those keywords, and a wire with a controlled
 * vocabulary must not silently acquire house terms.
 *
 * Hidden when there is nothing to add, so it never invites a no-op.
 */
function MergeBaseButton({ additions, onMerge }) {
  if (!additions.length) return null;
  return (
    <button type="button" className={styles.mergeBaseButton} onClick={onMerge}>
      Merge base ({additions.length})
    </button>
  );
}

/**
 * The banner naming the override scope, inside the surface.
 *
 * There is no equivalent on the base: the surface is ABSENT there, and a frame
 * appearing is what tells you the scope changed. Adding a matching banner to
 * the base would flatten that back into "two things that look alike".
 */
function ScopeBanner({ destinationName, template, count, onResetAll, copySources, onCopyFrom }) {
  return (
    <div className={styles.scopeBanner}>
      <div className={styles.scopeBannerEyebrow}>
        <span className={styles.scopeBannerDot} aria-hidden="true" />
        <span className={styles.scopeBannerEyebrowText}>
          Override scope — only this destination
        </span>
        <span className={styles.scopeBannerVia}>
          {template ? template.name : 'via the house default'}
        </span>
      </div>

      <div className={styles.scopeBannerRow}>
        <span className={styles.scopeBannerName}>{destinationName}</span>
        <span
          className={`${styles.scopeBannerChip} ${count === 0 ? styles.scopeBannerChipQuiet : ''}`}
        >
          {count === 0
            ? 'Follows base — no overrides'
            : `${count} field${count === 1 ? '' : 's'} differ${count === 1 ? 's' : ''}`}
        </span>
        {/* Copy another destination's overrides into this one. Client-side
            only: it fills the form and nothing is written until the step is
            saved, so it is undoable by Reset all or by closing without saving.
            Only destinations that HAVE overrides are offered -- copying an
            empty set is what Reset all already does. */}
        {copySources.length > 0 && (
          <label className={styles.scopeCopyFrom}>
            <span className={styles.scopeCopyFromLabel}>Copy from</span>
            <select
              className={styles.scopeCopyFromSelect}
              value=""
              onChange={(e) => {
                if (e.target.value) onCopyFrom(e.target.value);
                e.target.value = '';
              }}
            >
              <option value="">Choose a destination...</option>
              {copySources.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.count})
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          className={styles.scopeResetAll}
          onClick={onResetAll}
          disabled={count === 0}
        >
          Reset all
        </button>
      </div>

      <p className={styles.scopeBannerHelp}>
        Blank fields inherit this destination&rsquo;s template, then the shared
        base — inherited values appear as placeholders, never as saved values.
      </p>
    </div>
  );
}

/**
 * Label row for a field in override scope. Revert only appears once the field
 * actually carries an override, so its presence is itself the second signal
 * after the tinted input.
 */
function OverrideLabel({ label, overridden, onRevert, suppressed }) {
  return (
    <div className={styles.overrideLabelRow}>
      <label className={styles.label}>{label}</label>
      {/* A suppressed field is popped from the metadata AFTER every other
          layer, so whatever is typed here never reaches the file. Saying so on
          the label is the difference between a deliberate omission and an hour
          spent wondering why an edit does nothing. */}
      {suppressed && (
        <span className={styles.suppressedChip} title="This destination refuses this field">
          will be suppressed
        </span>
      )}
      {overridden && !suppressed && (
        <button type="button" className={styles.revertBtn} onClick={onRevert}>
          Revert
        </button>
      )}
    </div>
  );
}

/**
 * Build preview context from real match data + logged-in user for substitution.
 *
 * `user` should be the object returned by useAuth(); we use it so previews show the
 * current tenant's company name and photographer identity instead of a hardcoded stub.
 */
function buildPreviewContext(matchData, teams, user, bySlug) {
  const homeTeam = teams?.find((t) => String(t.id) === String(matchData?.home_team));
  const awayTeam = teams?.find((t) => String(t.id) === String(matchData?.away_team));
  const date = matchData?.start_time ? new Date(matchData.start_time) : null;

  const companyName = user?.company?.name || '';
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  const photogName = fullName || user?.username || '';

  // Season: explicit override wins, else derive from start_time (mirrors server)
  const season = matchData?.season || computeSeason(date);

  return {
    home_team: homeTeam?.name || '',
    away_team: awayTeam?.name || '',
    event: homeTeam && awayTeam ? `${homeTeam.name} vs ${awayTeam.name}` : '',
    competition: matchData?.competition ? getCompetitionLabel(bySlug, matchData.competition) : '',
    season,
    round: matchData?.round || '',
    venue: matchData?.venue || '',
    city: matchData?.city || '',
    state: matchData?.state || '',
    country: matchData?.country || '',
    day: date ? String(date.getDate()) : '',
    day0: date ? String(date.getDate()).padStart(2, '0') : '',
    month: date ? String(date.getMonth() + 1).padStart(2, '0') : '',
    monthname: date ? MONTHS[date.getMonth()] : '',
    year4: date ? String(date.getFullYear()) : '',
    match_phase: 'during',
    // These stay as examples since we don't know them yet
    persons: 'Santiago Gimenez, Quinten Timber',
    photog: photogName,
    copyright: companyName,
    company: companyName,
  };
}

/**
 * Live preview of rendered templates and static fields
 */
// Preview keys map back to the config field whose provenance they carry, so a
// rendered line can say where its value came from.
const PREVIEW_FIELD_FOR = {
  title: 'title_template',
  headline: 'headline_template',
  caption: 'caption_template',
  description: 'caption_template',
  credit: 'credit',
  source: 'source',
  keywords: 'keywords',
  category: 'category',
  supplemental_categories: 'supplemental_categories',
};

const SOURCE_LABEL = { override: 'Override', base: 'Base', template: 'Template' };

// On the base scope a blank channel field is not "empty" -- it means each
// destination's own template supplies it. Rendering nothing there implied the
// delivery would carry nothing.
const BASE_FALLTHROUGH_FIELDS = [
  { key: 'title_template', label: 'Title' },
  { key: 'headline_template', label: 'Headline' },
  { key: 'caption_template', label: 'Caption' },
  { key: 'credit', label: 'Credit' },
  { key: 'source', label: 'Source' },
];

// Preview keys are not always the metadata keys the exporter suppresses, so a
// suppression list has to be translated before it can hide a preview line.
const PREVIEW_KEY_FOR_METADATA = {
  title: ['title'],
  event: ['event_name'],
  rights: ['copyright_notice'],
  copyright_url: ['copyright_url'],
  usage_terms: ['usage_terms'],
};

// Which field a validator message belongs to, in the operator's words. The
// server keys errors by model field name; "caption_template" is not what the
// label above the box says.
const PREVIEW_ERROR_LABELS = {
  title_template: 'Title',
  headline_template: 'Headline',
  caption_template: 'Caption',
  credit: 'Credit',
  copyright_notice: 'Copyright notice',
};

function PreviewPanel({
  preview,
  previewErrors,
  config,
  provenance = {},
  scopeName,
  isBaseScope,
  suppressed = [],
  autoKeywords = [],
}) {
  const { bySlug } = useCompetitions();
  // Build full preview merging template preview + static fields
  const fullPreview = {};

  // Add rendered template fields from API preview
  if (preview) {
    Object.assign(fullPreview, preview);
  }

  // Add static fields from config. credit and copyright_notice already come from the
  // server-rendered preview (see preview fetch effect) so we don't copy them from the
  // raw config here — that would shadow {photog} substitution with literal template text.
  if (config) {
    if (config.event_name) fullPreview.event_name = config.event_name;
    // Mirrors _merge_keyword_lists: auto first, then configured, deduplicated
    // case-insensitively with first occurrence winning. Showing only the
    // configured list under-reported every destination -- three keywords in the
    // form against a dozen in the delivered file.
    const seen = new Set();
    const merged = [];
    for (const kw of [...autoKeywords, ...(config.keywords || [])]) {
      const k = String(kw).toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        merged.push(kw);
      }
    }
    if (merged.length > 0) fullPreview.keywords = merged.join(', ');
    if (config.category) fullPreview.category = config.category;
    if (config.supplemental_categories?.length > 0) {
      fullPreview.supplemental_categories = config.supplemental_categories.join(', ');
    }
    if (config.country_code) fullPreview.country_code = config.country_code;
  }

  // A destination that refuses a field must not advertise it here. The preview
  // is what an operator trusts instead of opening a delivered JPEG, so a line
  // shown for a field the exporter pops is the same lie as showing the base
  // where the template wins.
  const hiddenPreviewKeys = new Set(
    suppressed.flatMap((key) => PREVIEW_KEY_FOR_METADATA[key] || [key])
  );

  const entries = Object.entries(fullPreview).filter(
    ([key, value]) => value && !hiddenPreviewKeys.has(key)
  );

  const header = (
    <div className={styles.previewHeader}>
      <span>Preview</span>
      {scopeName && <span className={styles.previewScopeChip}>{scopeName}</span>}
    </div>
  );

  // An invalid token beats everything else on screen: until it is fixed there is
  // no preview to show, and the operator needs to know it is their token and not
  // the editor that is broken.
  if (previewErrors) {
    const general = previewErrors.__general__;
    const fields = Object.entries(previewErrors).filter(([key]) => key !== '__general__');
    return (
      <div className={styles.previewPanel}>
        {header}
        <div className={styles.previewErrorBox}>
          {general && <p className={styles.previewErrorGeneral}>{general}</p>}
          {fields.length > 0 && (
            <>
              <p className={styles.previewErrorGeneral}>
                This template cannot be rendered, so nothing below is being previewed.
              </p>
              {fields.map(([field, message]) => (
                <div key={field} className={styles.previewErrorItem}>
                  <span className={styles.previewErrorField}>
                    {PREVIEW_ERROR_LABELS[field] || field.replace(/_template$/, '')}:
                  </span>{' '}
                  <span className={styles.previewErrorMessage}>{message}</span>
                </div>
              ))}
              <p className={styles.previewErrorHint}>
                Check the token spelling against the variable list. Your other fields are
                unaffected and nothing has been saved.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className={styles.previewPanel}>
        {header}
        <p className={styles.previewEmpty}>Enter template values to see preview</p>
      </div>
    );
  }

  return (
    <div className={styles.previewPanel}>
      {header}
      <div className={styles.previewContent}>
        {entries.map(([key, value]) => {
          const field = PREVIEW_FIELD_FOR[key];
          const source = field ? provenance[field] : undefined;
          return (
            <div key={key} className={styles.previewItem}>
              <span className={styles.previewLabel}>
                {PREVIEW_LABELS[key] || key.replace(/_template$/, '').replace(/_/g, ' ')}:
                {source && (
                  <span
                    className={`${styles.previewTag} ${
                      styles[`previewTag${source[0].toUpperCase()}${source.slice(1)}`]
                    }`}
                  >
                    {SOURCE_LABEL[source]}
                  </span>
                )}
              </span>
              <span className={styles.previewValue}>{value}</span>
            </div>
          );
        })}
        {isBaseScope &&
          BASE_FALLTHROUGH_FIELDS.filter((f) => {
            const v = config?.[f.key];
            return Array.isArray(v) ? !v.length : !v;
          }).map((f) => (
            <div key={f.key} className={styles.previewItem}>
              <span className={styles.previewLabel}>{f.label}:</span>
              <span className={styles.previewFallthrough}>
                set per destination (falls through to its template)
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

/**
 * Generate pre-fill IPTC values from match data
 * Follows the VK Sportphoto XMP template format
 *
 * @param {Object} matchData - Form data from step 1
 * @param {Array} teams - Available teams list
 * @returns {Object} Pre-filled IPTC config values
 */
function generateIptcFromMatchData(matchData, teams, bySlug) {
  if (!matchData) return null;

  // Helper to get team by ID
  const getTeam = (teamId) => {
    if (!teamId || !teams) return null;
    return teams.find((t) => String(t.id) === String(teamId)) || null;
  };

  // Helper to get short team name (first word, e.g., "PSV" from "PSV Eindhoven")
  const getShortName = (fullName) => {
    if (!fullName) return null;
    return fullName.split(' ')[0].toLowerCase();
  };

  const homeTeam = getTeam(matchData.home_team);
  const awayTeam = getTeam(matchData.away_team);
  const homeTeamName = homeTeam?.name || null;
  const awayTeamName = awayTeam?.name || null;
  const homeShort = getShortName(homeTeamName);

  const competitionLabel = matchData.competition
    ? getCompetitionLabel(bySlug, matchData.competition)
    : null;
  const competitionLower = competitionLabel?.toLowerCase() || null;
  const competitionShortCode = matchData.competition
    ? getCompetitionShortCode(bySlug, matchData.competition)
    : '';

  // Season: explicit Match.season override wins, else derive from start_time
  const seasonStr = matchData.season || computeSeason(matchData.start_time) || null;

  // Build keywords array (all lowercase, matching XMP format)
  const keywords = [];

  // Static keywords
  keywords.push('wedstrijd');
  keywords.push('action');
  keywords.push('duel');

  // Season keywords
  if (seasonStr) {
    keywords.push(`season ${seasonStr}`);
    if (competitionLower) {
      keywords.push(`${competitionLower} ${seasonStr}`);
    }
    keywords.push(seasonStr);
  }

  // Competition keyword
  if (competitionLower) {
    keywords.push(competitionLower);
  }

  // Sport keywords (for football)
  if (matchData.sport === 'football' || !matchData.sport) {
    keywords.push('soccer');
    keywords.push('sport');
    keywords.push('football');
  }

  // Team combination keywords (all lowercase)
  if (homeTeamName && awayTeamName) {
    const homeLower = homeTeamName.toLowerCase();
    const awayLower = awayTeamName.toLowerCase();
    keywords.push(`${homeLower} v ${awayLower}`);
    keywords.push(`${homeShort} - ${awayLower}`);
    keywords.push(homeLower);
    keywords.push(awayLower);
  }

  // Build headline: "Competition: Home Team v Away Team"
  let headline = '';
  if (competitionLabel && homeTeamName && awayTeamName) {
    headline = `${competitionLabel}: ${homeTeamName} v ${awayTeamName}`;
  } else if (homeTeamName && awayTeamName) {
    headline = `${homeTeamName} v ${awayTeamName}`;
  }

  // Build caption template with variables (for Photo Mechanic style)
  const caption = `{city} - {persons} {match_phase} the ${seasonStr || '{season}'} ${competitionLabel || '{competition}'} season. The match is set between {event} at {location} on {day} {monthname} {year4} in {city}, {country}.
({copyright}/{photog})`;

  // Event name: prefer Step 1's Match Name if set, fall back to "Home and Away"
  let eventName = matchData.name || '';
  if (!eventName && homeTeamName && awayTeamName) {
    eventName = `${homeTeamName} and ${awayTeamName}`;
  }

  // Build title (dc:title) - Object Name format: HOME TEAM_AWAY TEAM_CODE_DDMMYY
  let title = '';
  if (homeTeamName && awayTeamName) {
    let dateStr = '';
    if (matchData.start_time) {
      const matchDate = new Date(matchData.start_time);
      const dd = String(matchDate.getDate()).padStart(2, '0');
      const mm = String(matchDate.getMonth() + 1).padStart(2, '0');
      const yy = String(matchDate.getFullYear()).slice(-2);
      dateStr = `${dd}${mm}${yy}`;
    }
    const parts = [homeTeamName.toUpperCase(), awayTeamName.toUpperCase()];
    if (competitionShortCode) parts.push(competitionShortCode);
    if (dateStr) parts.push(dateStr);
    title = parts.join('_');
  }

  // Use location data from matchData (entered in Step 1)
  return {
    title_template: title,
    keywords,
    headline_template: headline,
    caption_template: caption,
    event_name: eventName,
    // Location fields from Step 1
    location: matchData.venue || '',
    city: matchData.city || '',
    state: matchData.state || '',
    country: matchData.country || '',
    country_code: matchData.country_code || '',
    // Credit (PM variable, substituted per-photo at export)
    credit: '{photog}',
    // Category defaults
    category: 'S',
    supplemental_categories: ['SPO', 'SOC', 'FOC'],
  };
}

/**
 * IptcConfigStep component
 *
 * @param {Object} props
 * @param {Object} props.config - IPTC configuration object
 * @param {(config: Object) => void} props.onChange - Callback when config changes
 * @param {Object} props.matchData - Match form data from step 1
 * @param {Array} props.teams - Available teams for name lookup
 * @param {Function} props.fetchCompanyDefaults - API function to fetch company IPTC defaults
 * @param {Function} props.fetchPreview - API function to preview rendered templates
 */
export default function IptcConfigStep({
  config,
  onChange,
  matchData,
  teams,
  fetchCompanyDefaults,
  fetchPreview,
  selectedTargetIds = [],
  iptcByTarget = {},
  overridesByTarget = {},
  onOverridesChange,
  fetchFtpTargets,
  fetchIptcTemplates,
}) {
  const { bySlug, isLoading: competitionsLoading } = useCompetitions();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  // {field: message} from the server's own validator, or {__general__: ...} for
  // a failure that is not about the template text.
  const [previewErrors, setPreviewErrors] = useState(null);
  // Keywords the EXPORTER merges in on top of the configured ones. Shown
  // separately, and labelled, because they are not editable here and the
  // per-photo half of them does not exist until there is a photo.
  const [autoKeywords, setAutoKeywords] = useState([]);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const hasPrefilledRef = useRef(false);
  // null = the shared base ("All destinations"). Otherwise an ftp_target_id.
  const [activeTarget, setActiveTarget] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [templatesById, setTemplatesById] = useState({});

  // Names for the switcher. Step 2 holds ids; this step needs something an
  // editor recognises, and which template each destination writes through.
  useEffect(() => {
    if (!fetchFtpTargets) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchFtpTargets();
        if (cancelled) return;
        const all = [...(data?.defaults || []), ...(data?.available || [])];
        setDestinations(all);
      } catch (err) {
        console.error('Failed to load FTP targets for IPTC scoping:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchFtpTargets]);

  useEffect(() => {
    if (!fetchIptcTemplates) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchIptcTemplates();
        if (cancelled) return;
        const byId = {};
        (list || []).forEach((t) => {
          byId[String(t.id)] = t;
        });
        setTemplatesById(byId);
      } catch (err) {
        console.error('Failed to load IPTC templates:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchIptcTemplates]);

  const selectedIdSet = selectedTargetIds.map(String);
  const scopedDestinations = destinations.filter((d) => selectedIdSet.includes(String(d.id)));

  // A destination that vanished from step 2 must not stay selected here, or the
  // form would keep writing overrides to something that no longer delivers.
  useEffect(() => {
    if (activeTarget && !selectedIdSet.includes(String(activeTarget))) {
      setActiveTarget(null);
    }
  }, [activeTarget, selectedIdSet]);

  const activeOverrides = activeTarget ? overridesByTarget[String(activeTarget)] || {} : {};
  const isOverridden = (field) =>
    !!activeTarget && Object.prototype.hasOwnProperty.call(activeOverrides, field);
  // What this scope actually WRITES -- which on a destination is the override
  // and nothing else.
  //
  // An un-overridden field on a destination is EMPTY, and what it inherits
  // appears as a placeholder via `placeholderFor`. This used to return the
  // shared base as the input's value, which made `placeholderFor` unreachable
  // whenever the base had a value: every field looked like a saved value while
  // the badge said "FOLLOWS BASE - NO OVERRIDES", and after templates began to
  // outrank the base (10-08-2026) the boxes showed the house strings while the
  // preview beside them correctly showed the template's. Both the scope banner
  // and placeholderFor's own comment already described the behaviour
  // implemented here; only the code disagreed.
  //
  // Safe against the save path: `updateField` writes to the override map only
  // on a user edit and deletes the key when a field is emptied, so an empty box
  // stores nothing.
  const valueOf = (field) => {
    if (isOverridden(field)) return activeOverrides[field];
    if (activeTarget) return ARRAY_FIELDS.has(field) ? [] : '';
    return config[field];
  };

  // What the active scope actually DELIVERS, resolved the same way the server
  // resolves it: override > match config > template. The preview binds to this,
  // not to `config` -- a preview that always showed the base meant you could not
  // see what you were editing the moment you picked a destination.
  const activeTemplate = activeTarget
    ? templatesById[String(iptcByTarget[String(activeTarget)] || '')] || null
    : null;

  // Resolution has to report WHERE a value came from, not only what it is.
  // A preview that shows the right string without saying whether it came from
  // this destination, the shared base or the template leaves the editor
  // guessing which one to change to affect it.
  const resolveField = (field) => {
    const override = activeTarget ? activeOverrides[field] : undefined;
    const hasOverride = Array.isArray(override) ? override.length > 0 : !!override;
    if (hasOverride) return { value: override, source: 'override' };

    // The destination's template outranks the shared base, exactly like the
    // server. Naming a template is a per-destination decision, so a wire given
    // the Getty template keeps Getty's rendering when the house caption is
    // edited. A template only wins for the fields it actually declares.
    const fromTemplate = activeTemplate?.[field];
    const hasTemplate = Array.isArray(fromTemplate) ? fromTemplate.length > 0 : !!fromTemplate;
    if (hasTemplate) return { value: fromTemplate, source: 'template' };

    const base = config[field];
    const hasBase = Array.isArray(base) ? base.length > 0 : !!base;
    if (hasBase) return { value: base, source: 'base' };

    return { value: base, source: null };
  };

  const resolvedConfig = (() => {
    if (!activeTarget) return config;
    const resolved = { ...config };
    for (const field of OVERRIDABLE_FIELDS) {
      resolved[field] = resolveField(field).value;
    }
    return resolved;
  })();

  // {field: 'override' | 'base' | 'template'} for the preview's source tags.
  const provenance = (() => {
    const map = {};
    for (const field of OVERRIDABLE_FIELDS) {
      const { source } = resolveField(field);
      if (source) map[field] = source;
    }
    return map;
  })();

  const activeReferenceSpec =
    scopedDestinations.find((d) => String(d.id) === String(activeTarget))?.reference_spec ||
    null;

  const activeDestinationName =
    scopedDestinations.find((d) => String(d.id) === String(activeTarget))?.name || '';

  const resetAllOverrides = () => {
    if (!activeTarget) return;
    onOverridesChange?.({ ...overridesByTarget, [String(activeTarget)]: {} });
  };

  // Destinations whose overrides can be copied into the active one: every
  // OTHER selected destination that actually has some. Carries the count so
  // the picker says what you are about to take.
  const copySources = scopedDestinations
    .filter((d) => String(d.id) !== String(activeTarget))
    .map((d) => ({
      id: String(d.id),
      name: d.name,
      count: Object.keys(overridesByTarget[String(d.id)] || {}).length,
    }))
    .filter((d) => d.count > 0);

  // REPLACES the active destination's overrides with a copy of the source's.
  // Replace, not merge: "copy FTP 1 into FTP 2" means 2 ends up looking like 1,
  // and a merge would leave 2 carrying fields 1 does not have -- a third state
  // nobody asked for and which the badge count would not explain.
  //
  // Arrays are cloned, or both destinations would share one reference and
  // editing keywords on either would silently change the other.
  const copyOverridesFrom = (sourceId) => {
    if (!activeTarget || !sourceId) return;
    const source = overridesByTarget[String(sourceId)] || {};
    const copy = {};
    for (const [field, value] of Object.entries(source)) {
      if (!OVERRIDABLE_FIELDS.has(field)) continue;
      copy[field] = Array.isArray(value) ? [...value] : value;
    }
    onOverridesChange?.({ ...overridesByTarget, [String(activeTarget)]: copy });
  };

  // On a destination, a field with no override shows what it INHERITS as a
  // placeholder. Putting the inherited value in `value` instead would make
  // every field look overridden, and the badge count would disagree with the
  // form.
  const placeholderFor = (field, fallback) => {
    if (!activeTarget || isOverridden(field)) return fallback;
    const inherited = resolvedConfig[field];
    if (Array.isArray(inherited)) return inherited.length ? inherited.join(', ') : fallback;
    return inherited || fallback;
  };

  // The inherited TEXT, for seeding an override the operator wants to edit
  // rather than retype. Deliberately NOT `placeholderFor`: that falls back to an
  // example string ("{event} | {venue}"), and seeding a field with example text
  // would put a fabricated template into a real destination.
  const inheritedFor = (field) => {
    if (!activeTarget || isOverridden(field)) return '';
    const inherited = resolvedConfig[field];
    return typeof inherited === 'string' ? inherited : '';
  };

  const revertField = (field) => {
    if (!activeTarget) return;
    const next = { ...activeOverrides };
    delete next[field];
    onOverridesChange?.({ ...overridesByTarget, [String(activeTarget)]: next });
  };

  // Does this destination's template refuse the field outright? Suppression is
  // UNCONDITIONAL -- the key is popped whether or not anything filled it, which
  // is the entire point: what needed suppressing was never empty, it was filled
  // by the match config, a company default or an auto fallback. So it beats an
  // override too, and the form has to say so rather than accept an edit that
  // silently evaporates.
  const suppressedKeys = new Set(
    activeTarget ? activeTemplate?.suppress_fields || [] : []
  );
  const isSuppressed = (field) => suppressedKeys.has(METADATA_KEY_FOR_FIELD[field] || field);

  // ---- list fields -------------------------------------------------------
  //
  // Keywords and supplemental categories are shown as CHIPS in every scope --
  // one way of reading a list, not two. A destination that has not overridden
  // the field still shows the list it inherits, dimmed, rather than a
  // comma-joined placeholder string that looked like a different control.
  //
  // Nothing is stored until the operator edits: TagInput hands back the whole
  // resulting list, so adding or removing one chip adopts the inherited set as
  // this destination's own. That keeps "FOLLOWS BASE - NO OVERRIDES" true right
  // up to the moment it stops being true.
  const listValueOf = (field) =>
    (isOverridden(field) ? activeOverrides[field] : resolvedConfig[field]) || [];

  const isInheritedList = (field) => !!activeTarget && !isOverridden(field);

  // Base entries the resolved list does not already carry. Drives whether a
  // merge is worth offering at all -- an empty result means there is nothing to
  // merge and the button stays hidden.
  const baseAdditionsFor = (field) => {
    if (!activeTarget) return [];
    const base = Array.isArray(config[field]) ? config[field] : [];
    const current = listValueOf(field);
    return base.filter((item) => !current.includes(item));
  };

  // Explicit, never automatic. Resolution stays winner-takes-all -- a template
  // that names keywords means those keywords -- and this button is how an
  // operator says "and the shared ones too" for one destination.
  const mergeBaseInto = (field) => {
    const additions = baseAdditionsFor(field);
    if (!additions.length) return;
    updateField(field, [...listValueOf(field), ...additions]);
  };

  // Pre-fill IPTC fields on first render. Prefers Company defaults (title/headline/caption,
  // credit, copyright_notice, contact fields, usage_terms) and falls back to values derived
  // from Step 1 match data (keywords, title, event name, location, category).
  useEffect(() => {
    if (hasPrefilledRef.current) return;
    // The title code and the {competition} label come from the Competition
    // table; deriving before it has loaded would file the title without its
    // code. Wait for it when the match names a competition.
    if (matchData?.competition && competitionsLoading) return;

    // A destination carrying a template already supplies the renderings, so
    // prefilling the shared base from company defaults is noise: it fills boxes
    // nobody reads for those destinations. Leave the base blank and let each
    // template speak.
    //
    // The original reason was stronger and is now void: the base used to
    // OUTRANK the template, so a prefilled base silently made every chosen
    // template inert. Since 10-08-2026 the template wins, so this is a
    // tidiness guard rather than a correctness one.
    const anyTemplateChosen = selectedTargetIds.some((id) => !!iptcByTarget[String(id)]);

    // FILL BLANKS, never overwrite. This used to bail out entirely the moment
    // ANY field carried data, which made prefill all-or-nothing: a match whose
    // config had a city but no title never got a title, because the city
    // counted as "already configured". Editing an existing match therefore
    // derived nothing at all from step 1 -- the base arrived empty and the
    // per-destination variations sat on top of nothing.
    //
    // Every assignment below is now guarded on its OWN field being empty, so a
    // typed value is still never clobbered. "Load Company Defaults" stays the
    // explicit overwrite path.
    const isBlank = (field) => {
      const value = config[field];
      return Array.isArray(value) ? value.length === 0 : !value;
    };

    let cancelled = false;

    (async () => {
      let defaults = null;
      try {
        defaults = await fetchCompanyDefaults();
      } catch (err) {
        console.error('Failed to load company IPTC defaults:', err);
      }
      if (cancelled) return;

      const prefillData = generateIptcFromMatchData(matchData, teams, bySlug);
      const newConfig = { ...config };

      // RENDERINGS: Company > match-data fallback. Skipped when a template is
      // in play (see anyTemplateChosen) -- the base outranks the template, so
      // prefilling here would override every template picked in step 2 with the
      // house default and make step 2's choice do nothing.
      //
      // The FACTS below are prefilled either way: location, event and category
      // are properties of the match, no template supplies them, and leaving
      // them blank would just be lost work.
      // The TITLE is exempt from the template guard. It is a generated project
      // ID (HOME_AWAY_CODE_DDMMYY), not a rendering choice -- no template
      // supplies one, the company default for it is empty, and it is the field
      // the whole delivery is identified by. Skipping it left the most
      // important field on the form blank.
      if (isBlank('title_template')) {
        if (defaults?.title_template) {
          newConfig.title_template = defaults.title_template;
        } else if (prefillData?.title_template) {
          newConfig.title_template = prefillData.title_template;
        }
      }

      if (!anyTemplateChosen) {
        if (isBlank('headline_template')) {
          if (defaults?.headline_template) {
            newConfig.headline_template = defaults.headline_template;
          } else if (prefillData?.headline_template) {
            newConfig.headline_template = prefillData.headline_template;
          }
        }
        if (isBlank('caption_template')) {
          if (defaults?.caption_template) {
            newConfig.caption_template = defaults.caption_template;
          } else if (prefillData?.caption_template) {
            newConfig.caption_template = prefillData.caption_template;
          }
        }

        // Credit: Company > match-data fallback (a template, substituted at export)
        if (isBlank('credit')) {
          if (defaults?.credit) {
            newConfig.credit = defaults.credit;
          } else if (prefillData?.credit) {
            newConfig.credit = prefillData.credit;
          }
        }
        if (isBlank('source') && defaults?.source) {
          newConfig.source = defaults.source;
        }
      }

      // Copyright is never template-supplied, so it prefills regardless.
      if (isBlank('copyright_notice') && defaults?.copyright_notice) {
        newConfig.copyright_notice = defaults.copyright_notice;
      }

      // Company-only IPTC fields (admin-managed; Tank has no edit UI for these — they flow
      // into MatchIptcConfig so per-match override stays possible and preview stays accurate)
      if (defaults) {
        for (const key of [
          'copyright_url', 'usage_terms',
          'contact_email', 'contact_url', 'contact_address',
          'contact_city', 'contact_region', 'contact_postal_code',
          'contact_country', 'contact_phone',
        ]) {
          if (defaults[key] && isBlank(key)) newConfig[key] = defaults[key];
        }
      }

      // Match-derived fields (keywords, event name, location, category) — fall back only
      // FACTS derived from step 1. One rule for all of them: fill if blank.
      if (prefillData) {
        for (const key of [
          'keywords',
          'event_name',
          'location',
          'city',
          'state',
          'country',
          'country_code',
          'category',
          'supplemental_categories',
        ]) {
          const derived = prefillData[key];
          const hasValue = Array.isArray(derived) ? derived.length > 0 : !!derived;
          if (hasValue && isBlank(key)) newConfig[key] = derived;
        }
      }

      if (!cancelled) {
        onChange(newConfig);
        hasPrefilledRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config, matchData, teams, onChange, fetchCompanyDefaults, competitionsLoading, bySlug]);

  // Update a single field
  // Routes the edit to whichever scope is active. On a destination, an
  // overridable field writes an override; anything else still writes the base,
  // because the facts are shared and there is nowhere else for them to go.
  //
  // Clearing an override DELETES the key rather than storing "". A stored blank
  // would outrank the base and deliver nothing where the base had a value --
  // and the API strips blanks for the same reason, so keeping one here would
  // only disagree with what gets saved.
  const updateField = useCallback(
    (field, value) => {
      if (activeTarget && OVERRIDABLE_FIELDS.has(field)) {
        const current = overridesByTarget[String(activeTarget)] || {};
        const next = { ...current };
        const isEmpty = value === '' || value == null || (Array.isArray(value) && !value.length);
        if (isEmpty) {
          delete next[field];
        } else {
          next[field] = value;
        }
        onOverridesChange?.({ ...overridesByTarget, [String(activeTarget)]: next });
        return;
      }
      onChange({ ...config, [field]: value });
    },
    [activeTarget, config, onChange, onOverridesChange, overridesByTarget]
  );

  // Load company defaults — explicit button overwrites with Company's values for every
  // Company-sourced field. Use this to reset after edits.
  const handleLoadDefaults = async () => {
    try {
      setLoadingDefaults(true);
      const defaults = await fetchCompanyDefaults();
      // The company default for title_template is empty by design -- the title
      // is a generated project ID, not something an admin authors. Falling back
      // to `config.title_template` alone therefore left it blank forever on a
      // match that never had one. Generate it here the same way prefill does.
      const derived = generateIptcFromMatchData(matchData, teams, bySlug);
      const patch = {
        title_template:
          defaults.title_template || config.title_template || derived?.title_template || '',
        headline_template: defaults.headline_template || config.headline_template,
        caption_template: defaults.caption_template || config.caption_template,
        credit: defaults.credit || config.credit,
        copyright_notice: defaults.copyright_notice || config.copyright_notice,
      };
      // Keywords are never a company default; they are derived from the match.
      // Only ever ADDED to an empty list, so this cannot wipe typed keywords.
      if (!config.keywords?.length && derived?.keywords?.length) {
        patch.keywords = derived.keywords;
      }
      for (const key of [
        'copyright_url', 'usage_terms',
        'contact_email', 'contact_url', 'contact_address',
        'contact_city', 'contact_region', 'contact_postal_code',
        'contact_country', 'contact_phone',
      ]) {
        if (defaults[key]) patch[key] = defaults[key];
      }
      onChange({ ...config, ...patch });
    } catch (err) {
      console.error('Failed to load company defaults:', err);
    } finally {
      setLoadingDefaults(false);
    }
  };

  // Fetch preview when templates change - use real match data + logged-in user as context.
  // Runs credit + copyright_notice through the same pipeline so the preview reflects
  // what will actually be written to exported JPEGs (e.g. {photog} → real name).
  useEffect(() => {
    const templates = {
      title_template: resolvedConfig.title_template,
      headline_template: resolvedConfig.headline_template,
      caption_template: resolvedConfig.caption_template,
      credit: resolvedConfig.credit,
      copyright_notice: resolvedConfig.copyright_notice,
    };

    const hasContent = Object.values(templates).some((t) => t?.trim());
    if (!hasContent) {
      setPreview(null);
      setPreviewErrors(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const context = buildPreviewContext(matchData, teams, user, bySlug);
        const result = await fetchPreview(templates, context, matchData?.id || null);
        setPreview(result.preview);
        setAutoKeywords(result.auto_keywords || []);
        setPreviewErrors(null);
      } catch (err) {
        console.error('Preview fetch failed:', err);
        // A 400 here is ALWAYS a bad template token, and the server already
        // says which field and which token (`validate_templates_dict` returns
        // {field: message}). Swallowing it into the console meant a typo like
        // {completion} froze the preview on its last good render with no
        // explanation, which reads as "the editor is broken" rather than
        // "that token does not exist".
        const fieldErrors = err?.response?.data?.errors;
        if (fieldErrors && typeof fieldErrors === 'object') {
          setPreviewErrors(fieldErrors);
        } else {
          // Anything else (offline, 500, timeout) is not the operator's fault
          // and naming a field would be a lie.
          setPreviewErrors({
            __general__: 'Preview is unavailable right now. Your templates were not changed.',
          });
        }
        // The stale render has to go. Leaving it up next to an error message
        // invites reading the old preview as the new template's output.
        setPreview(null);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    resolvedConfig.title_template,
    resolvedConfig.headline_template,
    resolvedConfig.caption_template,
    resolvedConfig.credit,
    resolvedConfig.copyright_notice,
    fetchPreview,
    matchData,
    teams,
    user,
  ]);

  return (
    <div className={styles.iptcConfigStep}>
      <div className={styles.stepHeader}>
        <p className={styles.stepDescription}>
          Configure IPTC metadata that will be embedded in exported photos.
          Use variables like {'{home_team}'} for dynamic content.
          Case modifiers: {'{var:upper}'}, {'{var:lower}'}, {'{var:title}'}.
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLoadDefaults}
          loading={loadingDefaults}
        >
          Load Company Defaults
        </Button>
      </div>

      <div
        className={`${styles.scopeGrid} ${
          scopedDestinations.length === 0 ? styles.scopeGridNoRail : ''
        }`}
      >
        {scopedDestinations.length > 0 && (
          <ScopeNavRail
            destinations={scopedDestinations}
            activeTarget={activeTarget}
            onSelect={setActiveTarget}
            overridesByTarget={overridesByTarget}
            iptcByTarget={iptcByTarget}
          />
        )}

        <div className={styles.scopeMain}>
          {activeTarget !== null ? (
            /* OVERRIDE SCOPE. The surface is the state change: the base has no
               frame at all, so a frame APPEARING is what says you left it.
               Only the six channel-facing keys live here -- copyright and the
               match facts are base-only and the API rejects them as overrides,
               so offering them would be offering an edit that fails on save. */
            <div className={styles.scopeSurface}>
              <ScopeBanner
                destinationName={activeDestinationName}
                template={activeTemplate}
                count={Object.keys(activeOverrides).length}
                onResetAll={resetAllOverrides}
                copySources={copySources}
                onCopyFrom={copyOverridesFrom}
              />

              <div className={styles.overrideFields}>
                <TemplateInput
                  label="Title / Object Name (dc:title)"
                  value={valueOf('title_template') || ''}
                  onChange={(v) => updateField('title_template', v)}
                  placeholder={placeholderFor('title_template', 'HOME TEAM_AWAY TEAM_CODE_DDMMYY')}
                  inherited={inheritedFor('title_template')}
                  overridden={isOverridden('title_template')}
                  onRevert={() => revertField('title_template')}
                />
                <TemplateInput
                  label="Headline (photoshop:Headline)"
                  value={valueOf('headline_template') || ''}
                  onChange={(v) => updateField('headline_template', v)}
                  placeholder={placeholderFor('headline_template', '{event} | {venue}')}
                  inherited={inheritedFor('headline_template')}
                  overridden={isOverridden('headline_template')}
                  onRevert={() => revertField('headline_template')}
                />
                <TemplateInput
                  label="Caption (dc:description)"
                  value={valueOf('caption_template') || ''}
                  onChange={(v) => updateField('caption_template', v)}
                  placeholder={placeholderFor('caption_template', '{home_team} vs {away_team}')}
                  inherited={inheritedFor('caption_template')}
                  multiline
                  overridden={isOverridden('caption_template')}
                  onRevert={() => revertField('caption_template')}
                />

                <div className={styles.formGroup}>
                  <OverrideLabel
                    label="Keywords"
                    overridden={isOverridden('keywords')}
                    onRevert={() => revertField('keywords')}
                  suppressed={isSuppressed('keywords')}
                  />
                  <TagInput
                    value={listValueOf('keywords')}
                    onChange={(v) => updateField('keywords', v)}
                    inherited={isInheritedList('keywords')}
                    placeholder="Add keyword..."
                  />
                  <MergeBaseButton
                    additions={baseAdditionsFor('keywords')}
                    onMerge={() => mergeBaseInto('keywords')}
                  />
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <OverrideLabel
                      label="Credit"
                      overridden={isOverridden('credit')}
                      onRevert={() => revertField('credit')}
                    suppressed={isSuppressed('credit')}
                    />
                    <OverrideInput
                      className={`${styles.input} ${isOverridden('credit') ? styles.inputOverridden : ''}`}
                      value={valueOf('credit') || ''}
                      inherited={inheritedFor('credit')}
                      overridden={isOverridden('credit')}
                      onChange={(v) => updateField('credit', v)}
                      placeholder={placeholderFor('credit', 'Hero Images')}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <OverrideLabel
                      label="Source"
                      overridden={isOverridden('source')}
                      onRevert={() => revertField('source')}
                    suppressed={isSuppressed('source')}
                    />
                    <OverrideInput
                      className={`${styles.input} ${isOverridden('source') ? styles.inputOverridden : ''}`}
                      value={valueOf('source') || ''}
                      inherited={inheritedFor('source')}
                      overridden={isOverridden('source')}
                      onChange={(v) => updateField('source', v)}
                      placeholder={placeholderFor('source', 'Hero Images')}
                    />
                  </div>
                </div>

                {/* Credit, Source and Copyright are the channel identity trio:
                    a destination that needs one usually needs all three. Getty
                    wants {photog}/MB Media/Getty Images where the house line
                    says Box to Box. */}
                <div className={styles.formGroup}>
                  <OverrideLabel
                    label="Copyright Notice"
                    overridden={isOverridden('copyright_notice')}
                    onRevert={() => revertField('copyright_notice')}
                  suppressed={isSuppressed('copyright_notice')}
                  />
                  <OverrideInput
                    className={`${styles.input} ${isOverridden('copyright_notice') ? styles.inputOverridden : ''}`}
                    value={valueOf('copyright_notice') || ''}
                    inherited={inheritedFor('copyright_notice')}
                    overridden={isOverridden('copyright_notice')}
                    onChange={(v) => updateField('copyright_notice', v)}
                    placeholder={placeholderFor('copyright_notice', 'Box to Box Pictures')}
                  />
                </div>

                {/* By-line (dc:Creator). Blank = the photographer, which is what
                    every destination has always carried. Getty reads this as the
                    credited party and wants the agency, while the caption still
                    names the person via {photog}. */}
                <div className={styles.formGroup}>
                  <OverrideLabel
                    label="By-line (Creator)"
                    overridden={isOverridden('creator')}
                    onRevert={() => revertField('creator')}
                    suppressed={isSuppressed('creator')}
                  />
                  <OverrideInput
                    className={`${styles.input} ${isOverridden('creator') ? styles.inputOverridden : ''}`}
                    value={valueOf('creator') || ''}
                    inherited={inheritedFor('creator')}
                    overridden={isOverridden('creator')}
                    onChange={(v) => updateField('creator', v)}
                    placeholder={placeholderFor('creator', 'the photographer')}
                  />
                </div>

                {/* The destination's OWN declared reference field, if it has
                    one -- Getty wants a Transmission Reference, another wire may
                    want Special Instructions. Rendered from the spec the API
                    validates against, so the form cannot offer a value save
                    would refuse. */}
                {activeReferenceSpec && (
                  <div className={styles.formGroup}>
                    <OverrideLabel
                      label={activeReferenceSpec.label}
                      overridden={isOverridden(activeReferenceSpec.field)}
                      onRevert={() => revertField(activeReferenceSpec.field)}
                    />
                    <input
                      type={activeReferenceSpec.type === 'int' ? 'number' : 'text'}
                      min={activeReferenceSpec.min ?? undefined}
                      max={activeReferenceSpec.max ?? undefined}
                      maxLength={activeReferenceSpec.max_length ?? undefined}
                      className={`${styles.input} ${
                        isOverridden(activeReferenceSpec.field) ? styles.inputOverridden : ''
                      }`}
                      value={valueOf(activeReferenceSpec.field) || ''}
                      onChange={(e) =>
                        updateField(activeReferenceSpec.field, e.target.value)
                      }
                      placeholder={placeholderFor(
                        activeReferenceSpec.field,
                        `${activeReferenceSpec.label} for this match`
                      )}
                    />
                  </div>
                )}

                {/* Category was resolvable and tagged in the preview but had no
                    input here, so the form disagreed with what it was showing.
                    Every field the API accepts as an override is now editable
                    in this scope -- see OVERRIDABLE_FIELDS. */}
                <div className={styles.formGroup}>
                  <OverrideLabel
                    label="Category"
                    overridden={isOverridden('category')}
                    onRevert={() => revertField('category')}
                  suppressed={isSuppressed('category')}
                  />
                  <OverrideInput
                    className={`${styles.input} ${styles.inputNarrow} ${
                      isOverridden('category') ? styles.inputOverridden : ''
                    }`}
                    value={valueOf('category') || ''}
                    inherited={inheritedFor('category')}
                    overridden={isOverridden('category')}
                    onChange={(v) => updateField('category', v)}
                    placeholder={placeholderFor('category', 'S')}
                    maxLength={10}
                  />
                </div>
              </div>

              <div className={styles.overrideSubSection}>
                <TemplateInput
                  label="Persons shown (iptcExt:PersonInImage)"
                  value={valueOf('persons_template') || ''}
                  onChange={(v) => updateField('persons_template', v)}
                  placeholder={placeholderFor('persons_template', '{name} of {team}')}
                  inherited={inheritedFor('persons_template')}
                  overridden={isOverridden('persons_template')}
                  onRevert={() => revertField('persons_template')}
                />
                <TemplateInput
                  label="Featured Org. Name (iptcExt:OrganisationInImageName)"
                  value={valueOf('organisation_template') || ''}
                  onChange={(v) => updateField('organisation_template', v)}
                  placeholder={placeholderFor('organisation_template', '{display}')}
                  inherited={inheritedFor('organisation_template')}
                  overridden={isOverridden('organisation_template')}
                  onRevert={() => revertField('organisation_template')}
                />
                <p className={styles.personHint}>
                  Rendered once per tagged person. Placeholders:{' '}
                  {'{name} {team} {display} {first} {last} {prefix} {suffix} {jersey}'}. A blank
                  value collapses the phrase around it, so someone with no team
                  reads &quot;Referee Blom&quot;, never &quot;Referee Blom of&quot;.
                  {' '}Note {'{name}'} already includes the role prefix
                  (Referee, Coach); {'{display}'} never does.
                </p>
              </div>

              <div className={styles.overrideSubSection}>
                <OverrideLabel
                  label="Supplemental Categories"
                  overridden={isOverridden('supplemental_categories')}
                  onRevert={() => revertField('supplemental_categories')}
                suppressed={isSuppressed('supplemental_categories')}
                />
                <TagInput
                  value={listValueOf('supplemental_categories')}
                  onChange={(v) => updateField('supplemental_categories', v)}
                  inherited={isInheritedList('supplemental_categories')}
                  placeholder="Add category..."
                />
                <MergeBaseButton
                  additions={baseAdditionsFor('supplemental_categories')}
                  onMerge={() => mergeBaseInto('supplemental_categories')}
                />
              </div>

              <p className={styles.scopeFooterNote}>
                The match facts — location, event name, country code — are the
                same for every destination. Edit them on All destinations.
              </p>
            </div>
          ) : (
            <>
              {scopedDestinations.length > 0 && (
                <div className={styles.baseHeader}>
                  Shared base — all {scopedDestinations.length} destination
                  {scopedDestinations.length === 1 ? '' : 's'} follow these values
                </div>
              )}
          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>Templates</h4>

            <TemplateInput
              label="Title / Object Name (dc:title)"
              value={valueOf('title_template') || ''}
              onChange={(v) => updateField('title_template', v)}
              placeholder={placeholderFor('title_template', 'HOME TEAM_AWAY TEAM_CODE_DDMMYY')}
            />

            <TemplateInput
              label="Headline (photoshop:Headline)"
              value={valueOf('headline_template') || ''}
              onChange={(v) => updateField('headline_template', v)}
              placeholder={placeholderFor('headline_template', '{event} | {venue}')}
            />

            <TemplateInput
              label="Caption (dc:description)"
              value={valueOf('caption_template') || ''}
              onChange={(v) => updateField('caption_template', v)}
              placeholder="{home_team} vs {away_team}, {competition}, {day} {monthname} {year4}"
              multiline
            />
          </div>

          {/* FACTS about the match -- same wherever the frame goes, so there is
              no per-destination version of them to edit. */}
          {(
          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>Location</h4>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>City</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.city || ''}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="Rotterdam"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>State/Province</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.state || ''}
                  onChange={(e) => updateField('state', e.target.value)}
                  placeholder="Zuid-Holland"
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Country</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.country || ''}
                  onChange={(e) => updateField('country', e.target.value)}
                  placeholder="The Netherlands"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Country Code</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.country_code || ''}
                  onChange={(e) => updateField('country_code', e.target.value)}
                  placeholder="NLD"
                  maxLength={3}
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Sub-location (Venue)</label>
              <input
                type="text"
                className={styles.input}
                value={config.location || ''}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder="De Kuip"
              />
            </div>
          </div>

          )}

          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>Additional Metadata</h4>

            {(
              <div className={styles.formGroup}>
                <label className={styles.label}>Event Name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.event_name || ''}
                  onChange={(e) => updateField('event_name', e.target.value)}
                  placeholder="Eredivisie 2025-2026"
                />
              </div>
            )}

            {/* Getty's per-event code, written to
                photoshop:TransmissionReference. A FACT about the match, not a
                rendering, so it is the same wherever the frame goes and is not
                overridable. The column and the write path both existed; the
                field was simply unreachable from the form. */}
            {(
              <div className={styles.formGroup}>
                <label className={styles.label}>Transmission Reference</label>
                <input
                  type="text"
                  className={styles.input}
                  value={config.transmission_reference || ''}
                  onChange={(e) => updateField('transmission_reference', e.target.value)}
                  placeholder="Leave blank unless the wire supplied one"
                />
              </div>
            )}

            <TagInput
              label="Keywords"
              value={valueOf('keywords') || []}
              onChange={(v) => updateField('keywords', v)}
              placeholder="Add keyword..."
            />

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Credit</label>
                <input
                  type="text"
                  className={styles.input}
                  value={valueOf('credit') || ''}
                  onChange={(e) => updateField('credit', e.target.value)}
                  placeholder={placeholderFor('credit', 'VK Sportphoto')}
                />
              </div>
              {/* Source names the distribution CHANNEL, same as Credit, so it
                  is per destination too. It had no input at all, which left it
                  settable only by authoring a template. */}
              <div className={styles.formGroup}>
                <label className={styles.label}>Source</label>
                <input
                  type="text"
                  className={styles.input}
                  value={valueOf('source') || ''}
                  onChange={(e) => updateField('source', e.target.value)}
                  placeholder={placeholderFor('source', 'Hero Images')}
                />
              </div>
              <div className={styles.formGroup}>
                <OverrideLabel
                  label="Copyright Notice"
                  overridden={isOverridden('copyright_notice')}
                  onRevert={() => revertField('copyright_notice')}
                suppressed={isSuppressed('copyright_notice')}
                />
                <input
                  type="text"
                  className={`${styles.input} ${
                    isOverridden('copyright_notice') ? styles.inputOverridden : ''
                  }`}
                  value={valueOf('copyright_notice') || ''}
                  onChange={(e) => updateField('copyright_notice', e.target.value)}
                  placeholder={placeholderFor('copyright_notice', 'Copyright 2026 VK Sportphoto')}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Category</label>
              <input
                type="text"
                className={`${styles.input} ${styles.inputNarrow}`}
                value={valueOf('category') || ''}
                onChange={(e) => updateField('category', e.target.value)}
                placeholder={placeholderFor('category', 'S')}
                maxLength={10}
              />
            </div>
          </div>

          {/* Its own section, last. Paired next to Category it read as a
              sub-field of it, and a tag box is a different shape from a
              10-character code -- the row could never align. */}
          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>Supplemental Categories</h4>
            <TagInput
              value={valueOf('supplemental_categories') || []}
              onChange={(v) => updateField('supplemental_categories', v)}
              placeholder="Add category..."
            />
          </div>
            </>
          )}
        </div>

        {/* Preview follows the ACTIVE scope and resolves override > base >
            template, the server's own order. Each line carries where its value
            came from: the right string without its provenance leaves you
            guessing which scope to change to affect it. */}
        <div className={styles.previewCol}>
          <PreviewPanel
            preview={preview}
            previewErrors={previewErrors}
            config={resolvedConfig}
            provenance={provenance}
            scopeName={activeTarget === null ? 'All destinations' : activeDestinationName}
            isBaseScope={activeTarget === null}
            suppressed={activeTarget ? activeTemplate?.suppress_fields || [] : []}
            autoKeywords={autoKeywords}
          />
        </div>
      </div>
    </div>
  );
}
